/* eslint-disable max-lines-per-function */
/*eslint max-lines-per-function: ["error", 230]*/
import React, { useCallback } from 'react';
import saveAs from 'file-saver';
import { Card } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useParams } from 'react-router-dom';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { JudgmentsSearch } from '../../components/Judgments/JudgmentsSearch';
import Button from '../../../common/components/Button';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { ResponseError } from '../../../common/spdCore/autogenerated/spdApiClient';

type ParamTypes = {
  psychologistId?: string;
  companyId?: string;
  registerId?: string;
  healthcareFacilityId?: string;
  dateFromYear?: string;
  dateFromMonth?: string;
  dateFromDay?: string;
  dateToYear?: string;
  dateToMonth?: string;
  dateToDay?: string;
  onlyMonthlySettlement?: string;
  withHealthcareFacility?: string;
  withRegister?: string;
};

export const ReportCompaniesDetailsView: React.FC = () => {
  const spd = useSpdCore();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const {
    psychologistId,
    companyId,
    registerId,
    healthcareFacilityId,
    dateFromYear,
    dateFromMonth,
    dateFromDay,
    dateToYear,
    dateToMonth,
    dateToDay,
    onlyMonthlySettlement,
    withHealthcareFacility,
    withRegister,
  } = useParams<ParamTypes>() as {
    psychologistId?: string;
    companyId?: string;
    registerId?: string;
    healthcareFacilityId?: string;
    dateFromYear?: string;
    dateFromMonth?: string;
    dateFromDay?: string;
    dateToYear?: string;
    dateToMonth?: string;
    dateToDay?: string;
    onlyMonthlySettlement?: string;
    withHealthcareFacility?: string;
    withRegister?: string;
  };

  const handleOnExport = async () => {
    setIsLoading(true);
    await spd.pdfPrinter.printReportJudgments(
      '',
      psychologistId !== 'undefined' ? psychologistId : undefined,
      companyId !== 'undefined' ? companyId : undefined,
      registerId !== 'undefined' ? registerId : undefined,
      healthcareFacilityId !== 'undefined' ? healthcareFacilityId : undefined,
      dateFromYear && dateFromYear !== 'undefined'
        ? new Date(
            Number(dateFromYear),
            Number(dateFromMonth),
            Number(dateFromDay),
          )
        : undefined,
      dateToYear && dateToYear !== 'undefined'
        ? new Date(
            Number(dateToYear),
            Number(dateToMonth),
            Number(dateToDay),
            23,
            59,
            59,
          )
        : undefined,
      onlyMonthlySettlement,
      withHealthcareFacility === 'true',
      withRegister === 'true',
    );
    setIsLoading(false);
  };

  const handleOnExportExcel = async () => {
    setIsLoading(true);
    const currentDate = new Date().toISOString().split('T')[0];

    spd.reportsExcel
      .downloadReportJudgments(
        `zestawienie_orzeczen_${currentDate}.xlsx`,
        '',
        psychologistId !== 'undefined' ? psychologistId : undefined,
        companyId !== 'undefined' ? companyId : undefined,
        registerId !== 'undefined' ? registerId : undefined,
        healthcareFacilityId !== 'undefined' ? healthcareFacilityId : undefined,
        dateFromYear && dateFromYear !== 'undefined'
          ? new Date(
              Number(dateFromYear),
              Number(dateFromMonth),
              Number(dateFromDay),
            )
          : undefined,
        dateToYear && dateToYear !== 'undefined'
          ? new Date(
              Number(dateToYear),
              Number(dateToMonth),
              Number(dateToDay),
              23,
              59,
              59,
            )
          : undefined,
        onlyMonthlySettlement,
        withHealthcareFacility === 'true',
        withRegister === 'true',
      )
      .then(blob => {
        if (blob) {
          saveAs(blob, `Zestawienie_szczegolowe_${currentDate}.xlsx`);
          setIsLoading(false);
        }
      })
      .catch(async response => {
        const err = await responseErrors(response as ResponseError);
        toast.error(err?.[0]);
        setIsLoading(false);
      });
  };

  const getData = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getCompaniesDetailsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        psychologistId !== 'undefined' ? psychologistId : undefined,
        companyId !== 'undefined' ? companyId : undefined,
        registerId !== 'undefined' ? registerId : undefined,
        healthcareFacilityId !== 'undefined' ? healthcareFacilityId : undefined,
        dateFromYear && dateFromYear !== 'undefined'
          ? new Date(
              Number(dateFromYear),
              Number(dateFromMonth),
              Number(dateFromDay),
            )
          : undefined,
        dateToYear && dateToYear !== 'undefined'
          ? new Date(
              Number(dateToYear),
              Number(dateToMonth),
              Number(dateToDay),
              23,
              59,
              59,
            )
          : undefined,
        onlyMonthlySettlement,
        withHealthcareFacility === 'true',
        withRegister === 'true',
      ),
    [
      spd.reports,
      psychologistId,
      companyId,
      registerId,
      healthcareFacilityId,
      dateFromYear,
      dateFromMonth,
      dateFromDay,
      dateToYear,
      dateToMonth,
      dateToDay,
      onlyMonthlySettlement,
      withHealthcareFacility,
      withRegister,
    ],
  );

  const getCount = useCallback(
    (filterText?: string) =>
      spd.reports.getCompaniesDetailsCount(
        filterText,
        psychologistId !== 'undefined' ? psychologistId : undefined,
        companyId !== 'undefined' ? companyId : undefined,
        registerId !== 'undefined' ? registerId : undefined,
        healthcareFacilityId !== 'undefined' ? healthcareFacilityId : undefined,
        dateFromYear && dateFromYear !== 'undefined'
          ? new Date(
              Number(dateFromYear),
              Number(dateFromMonth),
              Number(dateFromDay),
            )
          : undefined,
        dateToYear && dateToYear !== 'undefined'
          ? new Date(
              Number(dateToYear),
              Number(dateToMonth),
              Number(dateToDay),
              23,
              59,
              59,
            )
          : undefined,
        onlyMonthlySettlement,
        withHealthcareFacility === 'true',
        withRegister === 'true',
      ),
    [
      spd.reports,
      psychologistId,
      companyId,
      registerId,
      healthcareFacilityId,
      dateFromYear,
      dateFromMonth,
      dateFromDay,
      dateToYear,
      dateToMonth,
      dateToDay,
      onlyMonthlySettlement,
      withHealthcareFacility,
      withRegister,
    ],
  );

  return (
    <>
      <ToastContainer />
      <Card>
        <Card.Header>{'Zestawienie szczegółowe'}</Card.Header>
        <Card.Body>
          <Button
            variant="outline-primary"
            onClick={handleOnExport}
            isLoading={isLoading}
            className="me-2"
          >
            Eksportuj
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleOnExportExcel}
            isLoading={isLoading}
          >
            Eksportuj do excela
          </Button>
          <JudgmentsSearch getData={getData} getCount={getCount} />
        </Card.Body>
      </Card>
    </>
  );
};
